import { useEditorState as useTipTapEditorState } from "@tiptap/react"
import {
	IconTextBoldOutline18 as BoldIcon,
	IconCode2Outline18 as Code2Icon,
	IconSquareCodeOutline18 as CodeBlockIcon,
	IconTextItalicOutline18 as ItalicIcon,
	IconLinkOutline18 as LinkIcon,
	IconBulletListOutline18 as ListIcon,
	IconOrderedListOutline18 as ListOrderedIcon,
	IconQuoteOutline18 as QuoteIcon,
	IconTextStrikethroughOutline18 as StrikethroughIcon,
	IconLinkSlashOutline18 as UnlinkIcon,
} from "nucleo-ui-outline-18"

import { Tooltip } from "@productlane/ui"

import type { Editor } from "@tiptap/react"
import { EditorMenuButton } from "@/components/editor/menu/editor-menu-button"

/**
 * Small component for BubbleMenu format buttons that subscribes to editor
 * active states independently, so BottomBar doesn't re-render on selection changes.
 */
export const BubbleMenuFormatButtons = ({
	editor,
	onAddLink,
	showLinkButtons = true,
}: {
	editor: Editor
	onAddLink?: () => void
	showLinkButtons?: boolean
}) => {
	const activeState = useTipTapEditorState({
		editor,
		selector: ({ editor: e }) => ({
			bold: e?.isActive("bold") ?? false,
			italic: e?.isActive("italic") ?? false,
			strike: e?.isActive("strike") ?? false,
			blockquote: e?.isActive("blockquote") ?? false,
			bulletList: e?.isActive("bulletList") ?? false,
			orderedList: e?.isActive("orderedList") ?? false,
			code: e?.isActive("code") ?? false,
			codeBlock: e?.isActive("codeBlock") ?? false,
			link: e?.isActive("link") ?? false,
		}),
	})

	return (
		<div className="flex gap-0.5">
			<Tooltip shouldWrapChildren label="Bold">
				<EditorMenuButton
					onClick={() =>
						editor
							.chain()
							.focus(undefined, { scrollIntoView: false })
							.toggleBold()
							.run()
					}
					isActive={activeState?.bold ?? false}
				>
					<BoldIcon className="h-4 w-4" />
				</EditorMenuButton>
			</Tooltip>
			<Tooltip shouldWrapChildren label="Italic">
				<EditorMenuButton
					onClick={() =>
						editor
							.chain()
							.focus(undefined, { scrollIntoView: false })
							.toggleItalic()
							.run()
					}
					isActive={activeState?.italic ?? false}
				>
					<ItalicIcon className="h-4 w-4" />
				</EditorMenuButton>
			</Tooltip>
			<Tooltip shouldWrapChildren label="Strikethrough">
				<EditorMenuButton
					onClick={() =>
						editor
							.chain()
							.focus(undefined, { scrollIntoView: false })
							.toggleStrike()
							.run()
					}
					isActive={activeState?.strike ?? false}
				>
					<StrikethroughIcon className="h-4 w-4" />
				</EditorMenuButton>
			</Tooltip>

			<Tooltip shouldWrapChildren label="Blockquote">
				<EditorMenuButton
					onClick={() =>
						editor
							.chain()
							.focus(undefined, { scrollIntoView: false })
							.toggleBlockquote()
							.run()
					}
					isActive={activeState?.blockquote ?? false}
				>
					<QuoteIcon className="h-4 w-4" />
				</EditorMenuButton>
			</Tooltip>
			<Tooltip shouldWrapChildren label="Bullet list">
				<EditorMenuButton
					onClick={() =>
						editor
							.chain()
							.focus(undefined, { scrollIntoView: false })
							.toggleBulletList()
							.run()
					}
					isActive={activeState?.bulletList ?? false}
				>
					<ListIcon className="h-4 w-4" />
				</EditorMenuButton>
			</Tooltip>
			<Tooltip shouldWrapChildren label="Numbered list">
				<EditorMenuButton
					onClick={() =>
						editor
							.chain()
							.focus(undefined, { scrollIntoView: false })
							.toggleOrderedList()
							.run()
					}
					isActive={activeState?.orderedList ?? false}
				>
					<ListOrderedIcon className="h-4 w-4" />
				</EditorMenuButton>
			</Tooltip>
			<Tooltip shouldWrapChildren label="Inline code">
				<EditorMenuButton
					onClick={() =>
						editor
							.chain()
							.focus(undefined, { scrollIntoView: false })
							.toggleCode()
							.run()
					}
					isActive={activeState?.code ?? false}
				>
					<Code2Icon className="h-4 w-4" />
				</EditorMenuButton>
			</Tooltip>
			<Tooltip shouldWrapChildren label="Code block">
				<EditorMenuButton
					onClick={() =>
						editor
							.chain()
							.focus(undefined, { scrollIntoView: false })
							.toggleCodeBlock()
							.run()
					}
					isActive={activeState?.codeBlock ?? false}
				>
					<CodeBlockIcon className="h-4 w-4" />
				</EditorMenuButton>
			</Tooltip>
			{showLinkButtons && (
				<>
					<Tooltip shouldWrapChildren label="Link">
						<EditorMenuButton
							onClick={(e) => {
								e.preventDefault()
								onAddLink?.()
							}}
							isActive={false}
						>
							<LinkIcon className="h-4 w-4" />
						</EditorMenuButton>
					</Tooltip>
					{activeState?.link && (
						<Tooltip shouldWrapChildren label="Remove link">
							<EditorMenuButton
								onClick={(e) => {
									e.preventDefault()
									editor
										.chain()
										.focus(undefined, { scrollIntoView: false })
										.unsetLink()
										.run()
								}}
								isActive={activeState.link}
							>
								<UnlinkIcon className="h-4 w-4" />
							</EditorMenuButton>
						</Tooltip>
					)}
				</>
			)}
		</div>
	)
}
